import { useState, useCallback, useEffect } from 'react';
import { FaMagnifyingGlass } from 'react-icons/fa6';

import { useAppContext } from '../contextApi/context';
import { vehicleTypeFilterOptions } from '../constants/vehicleTypes';
import {
    getIssuesByVehicleReport,
    getReportIssuesByVehicleTotalValue,
    getVehicleIssuesDetails,
    getVehicleDetailsTotalValue,
} from '../services/ReportsService';
import { exportIssuesByVehicleReport, exportIssueVehicleDetailsReport } from '../services/ExportService';
import TableContainer from '../components/table/TableContainer';
import { formatCurrency } from '../utils/numbers';
import { vehicleTypeLabel } from '../utils/translation';
import { AddButton, TableActionButton } from '../styles/StyledComponents';

const ReportIssuesByVehicle = () => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    let [showVehicleDetailsTable, setShowVehicleDetailsTable] = useState(false);
    let [currentVehicle, setCurrentVehicle] = useState({});
    let [parentFilters, setParentFilters] = useState([]);
    const [updateTable, setUpdateTable] = useState(false);
    let [tableTotalValue, setTableTotalValue] = useState(null);
    let [detailsTotalValue, setDetailsTotalValue] = useState(null);

    // ----- API Fetch Actions
    const fetchTotalValueData = useCallback(
        async (filters = []) => {
            return await getReportIssuesByVehicleTotalValue(loggedUser.token, filters);
        },
        [loggedUser.token]
    );

    const fetchDetailsTotalValueData = useCallback(
        async (filters = [], vehicle) => {
            return await getVehicleDetailsTotalValue(loggedUser.token, filters, vehicle);
        },
        [loggedUser.token]
    );

    // ----- Hooks
    useEffect(() => {
        fetchTotalValueData().then((data) => {
            setTableTotalValue(data);
        });
    }, [fetchTotalValueData]);

    // ----- Actions
    const handleShowVehicleDetailsTable = (vehicleId, plate) => {
        setShowVehicleDetailsTable(false);

        setTimeout(() => {
            setCurrentVehicle({ vehicleId, plate });
            setUpdateTable(!updateTable);
            setShowVehicleDetailsTable(true);
        }, 100);

        fetchDetailsTotalValueData(parentFilters, { vehicleId, plate }).then((data) => {
            setDetailsTotalValue(data);
        });
    };

    const handleDataAfterFiltering = (filters) => {
        fetchTotalValueData(filters).then((data) => {
            setTableTotalValue(data);
        });

        let parentFilters = JSON.parse(JSON.stringify(filters));
        parentFilters = parentFilters
            .filter((pf) => pf.columnName === 'article' || pf.columnName === 'eventDate' || pf.columnName === 'issueType')
            .map((f) => {
                if (f.columnName === 'article') {
                    f.columnName = 'parentArticle';
                }
                return f;
            });
        setParentFilters(parentFilters);

        setShowVehicleDetailsTable(false);
    };

    const handleDetailsDataAfterFiltering = (filters) => {
        fetchDetailsTotalValueData(filters, currentVehicle).then((data) => {
            setDetailsTotalValue(data);
        });
    };

    // ----- Constants
    const Actions = ({ row }) => (
        <div className='d-flex'>
            <TableActionButton className='btn btn-primary' onClick={() => handleShowVehicleDetailsTable(row.id, row.plate)}>
                <FaMagnifyingGlass />
                Ver Detalles
            </TableActionButton>
        </div>
    );

    const columns = [
        {
            name: 'Placa',
            selector: (row) => row.plate,
            sortable: true,
            sortField: 'plate',
        },
        {
            name: 'Valor total',
            selector: (row) => `₡${formatCurrency(row.totalValue)}`,
            sortable: true,
            sortField: 'totalValue',
        },
        {
            name: 'Tipo de vehículo',
            selector: (row) => vehicleTypeLabel(row.type),
        },
        {
            name: 'Acciones',
            width: '150px',
            cell: (row) => <Actions row={row} />,
        },
    ];

    const filters = [
        {
            column: 'plate',
            input: 'text',
            label: 'Placa',
            operation: 'like',
            length: 20,
        },
        {
            column: 'type',
            input: 'select',
            label: 'Tipo de vehículo',
            options: vehicleTypeFilterOptions,
            operation: 'equal',
        },
        {
            column: 'article',
            input: 'text',
            label: 'Artículo',
            operation: 'like',
            length: 50,
        },
        {
            column: 'issueType',
            input: 'text',
            label: 'Tipo de despacho',
            operation: 'like',
            length: 50,
        },
        {
            column: 'totalValue',
            input: 'range',
            label: 'Valor total',
            operation: 'between',
            length: 15,
        },
        {
            column: 'eventDate',
            input: 'dates',
            label: 'Fecha de despacho',
            operation: 'between',
        },
    ];

    const exportOptions = {
        handler: exportIssuesByVehicleReport,
    };

    const exportDetailstOptions = {
        handler: exportIssueVehicleDetailsReport,
    };

    const vehicleDetailsTableColumns = [
        {
            name: 'Artículo',
            selector: (row) => row.name,
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Cantidad',
            selector: (row) => row.amount,
            sortable: true,
            sortField: 'amount',
        },
        {
            name: 'Valor promedio',
            selector: (row) => `₡${formatCurrency(row.averageValue)}`,
            sortable: true,
            sortField: 'averageValue',
        },
        {
            name: 'Valor total',
            selector: (row) => `₡${formatCurrency(row.totalValue)}`,
            sortable: true,
            sortField: 'totalValue',
        },
    ];

    const vehicleDetailsTableFilters = [
        {
            column: 'name',
            input: 'text',
            label: 'Artículo',
            operation: 'like',
            length: 50,
        },
    ];

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1 className='mb-5'>Reporte de Despachos por Placa</h1>

            <TableContainer
                columns={columns}
                fetchDataHandler={getIssuesByVehicleReport}
                updateTable={true}
                filters={filters}
                afterFilteringHandler={handleDataAfterFiltering}
                exportOptions={exportOptions}
                totalValue={tableTotalValue}
            />

            {showVehicleDetailsTable && (
                <div className='my-5'>
                    <h3>Detalles para vehículo con placa: {currentVehicle.plate}</h3>

                    <div className='d-flex justify-content-end'>
                        <AddButton className='btn btn-primary' onClick={() => setShowVehicleDetailsTable(false)}>
                            Ocultar detalles
                        </AddButton>
                    </div>

                    <TableContainer
                        afterFilteringHandler={handleDetailsDataAfterFiltering}
                        columns={vehicleDetailsTableColumns}
                        fetchDataHandler={getVehicleIssuesDetails}
                        updateTable={updateTable}
                        filters={vehicleDetailsTableFilters}
                        pathVariables={currentVehicle}
                        exportOptions={exportDetailstOptions}
                        parentFilters={parentFilters}
                        totalValue={detailsTotalValue}
                    />
                </div>
            )}
        </div>
    );
};

export default ReportIssuesByVehicle;
